/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file sipCallEvents.js
 * @description
 * In-memory listener for the calls of THIS device's registered SIP account:
 * `started` (outgoing), `received` (incoming, answered) and `missed` (incoming,
 * not received — rejected, cancelled or unanswered).
 *
 * Security / GDPR: events come only from the device's own SIP session
 * (CallProvider), so they are scoped to that account by construction. Nothing is
 * persisted, logged or sent anywhere; the number lives only in the event object
 * handed to subscribers. The matching history is fetched server-side scoped via
 * `listSipScopedCalls` (voipApi.js).
 *
 * Pure JavaScript (ESM), no TypeScript.
 */

import {useEffect, useRef} from 'react';

/** Call kinds a subscriber can listen for. */
export const SIP_CALL_KINDS = Object.freeze({
    STARTED: 'started',
    RECEIVED: 'received',
    MISSED: 'missed',
});

/** `GET /api/voip/calls` `filter` value per kind (started=outgoing, received=answered incoming, missed=not received). */
const KIND_FILTER = Object.freeze({
    [SIP_CALL_KINDS.STARTED]: 'outgoing',
    [SIP_CALL_KINDS.RECEIVED]: 'received',
    [SIP_CALL_KINDS.MISSED]: 'missed',
});

/**
 * Backend list filter for a call kind.
 * @param {string} kind One of {@link SIP_CALL_KINDS}; anything else means "all".
 * @returns {string}
 */
export const filterForKind = (kind) => KIND_FILTER[kind] || 'all';

const listeners = new Set();

/**
 * Subscribe to SIP call events.
 * @param {(event: {kind: string, direction: string, number: string, at: number, durationSeconds?: number}) => void} listener
 * @returns {() => void} Unsubscribe.
 */
export function subscribeSipCalls(listener) {
    if (typeof listener !== 'function') {
        return () => {};
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Fan an event out to every subscriber. A throwing subscriber never affects the
 * call flow or the other subscribers.
 * @param {object} event
 */
export function emitSipCall(event) {
    [...listeners].forEach((listener) => {
        try {
            listener(event);
        } catch (_error) { /* isolate subscriber failures */ }
    });
}

/**
 * Per-call state machine turning the SIP session lifecycle into the three
 * events. Idempotent: each call produces at most one event per transition.
 *
 * @param {(event: object) => void} [emit] Event sink (defaults to {@link emitSipCall}).
 * @param {() => number} [now] Clock, injectable for tests.
 * @returns {{begin: Function, answered: Function, end: Function}}
 */
export function createSipCallTracker(emit = emitSipCall, now = Date.now) {
    let call = null;

    return {
        /**
         * A call started on the account. Outgoing emits `started` right away.
         * @param {'incoming'|'outgoing'} direction
         * @param {string} number Remote number (kept in memory only).
         */
        begin(direction, number) {
            call = {direction, number: String(number || ''), answered: false, startedAt: now()};
            if (direction === 'outgoing') {
                emit({kind: SIP_CALL_KINDS.STARTED, direction, number: call.number, at: call.startedAt});
            }
        },
        /** The remote party / we picked up. Incoming emits `received` once. */
        answered() {
            if (!call || call.answered) {
                return;
            }
            call.answered = true;
            if (call.direction === 'incoming') {
                emit({kind: SIP_CALL_KINDS.RECEIVED, direction: call.direction, number: call.number, at: now()});
            }
        },
        /**
         * The call is over. An incoming call that was never answered emits `missed`.
         * @param {number} [durationSeconds]
         */
        end(durationSeconds) {
            if (!call) {
                return;
            }
            const finished = call;
            call = null;
            if (finished.direction === 'incoming' && !finished.answered) {
                emit({
                    kind: SIP_CALL_KINDS.MISSED,
                    direction: finished.direction,
                    number: finished.number,
                    at: now(),
                    durationSeconds: Number(durationSeconds) || 0,
                });
            }
        },
    };
}

/**
 * React hook: run `handler` for SIP call events (optionally only some kinds).
 * @param {(event: object) => void} handler
 * @param {string[]} [kinds] Subset of {@link SIP_CALL_KINDS} values; omitted = all.
 */
export function useSipCallEvents(handler, kinds) {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;
    const kindsKey = Array.isArray(kinds) ? kinds.join(',') : '';

    useEffect(() => subscribeSipCalls((event) => {
        if (!kindsKey || kindsKey.split(',').includes(event.kind)) {
            handlerRef.current && handlerRef.current(event);
        }
    }), [kindsKey]);
}
